import { useState, useEffect, useMemo } from 'react';
import { Icon, Select } from '@nazka/nazka.mapframe.components';
import { faWarning } from '@fortawesome/pro-regular-svg-icons';

import { useIndicators } from '@services/query.service';
import { useTranslation } from '@utils/i18n/i18n';

import IndicatorCard from './IndicatorCard/IndicatorCard';
import IndicatorsGraph from './IndicatorsGraph/IndicatorsGraph';
import ContributingParameters from './ContributingParameters/ContributingParameters';
import { getThreshold, getClosestMonth } from './dashboard.utils';
import { INDICATORS, MONTH_NAMES } from './dashboard.constants';

import './dashboard.css';

function Dashboard() {
  const { t } = useTranslation();

  const [year, setYear] = useState();
  const [month, setMonth] = useState();

  const { data: indicators } = useIndicators();

  const yearOptions = useMemo(() => {
    if (!indicators) return [];
    const years = [...new Set(indicators.map(d => d.year))].sort((a, b) => b - a);
    return years.map(y => ({ id: y, label: String(y) }));
  }, [indicators]);

  const monthOptions = useMemo(() => {
    if (!indicators || year == null) return [];
    const months = [...new Set(
      indicators.filter(d => d.year === year).map(d => d.month),
    )].sort((a, b) => a - b);
    return months.map(m => ({ id: m, label: MONTH_NAMES[m] }));
  }, [indicators, year]);

  const monthsByYear = useMemo(() => {
    if (!indicators?.length) {
      return {};
    }

    return indicators.reduce((acc, indicator) => {
      if (!acc[indicator.year]) {
        acc[indicator.year] = [];
      }

      if (!acc[indicator.year].includes(indicator.month)) {
        acc[indicator.year].push(indicator.month);
        acc[indicator.year].sort((a, b) => a - b);
      }

      return acc;
    }, {});
  }, [indicators]);

  useEffect(() => {
    if (year == null && yearOptions.length > 0) {
      setYear(yearOptions[0].id);
    }
  }, [yearOptions, year]);

  useEffect(() => {
    if (year == null) return;
    const validMonthIds = monthOptions.map(m => m.id);
    const hasSelectedMonth = month != null && validMonthIds.includes(month);

    if (hasSelectedMonth) {
      return;
    }

    const nextMonth = getClosestMonth(validMonthIds);

    if (nextMonth !== month) {
      setMonth(nextMonth);
    }
  }, [monthOptions, month, year]);

  const data = useMemo(() => {
    if (!indicators?.length) {
      return [];
    }

    return [...indicators]
      .sort((a, b) => {
        if (a.year !== b.year) {
          return a.year - b.year;
        }

        return a.month - b.month;
      })
      .map((indicator, index) => ({
        ...indicator,
        index: index + 1,
      }));
  }, [indicators]);

  const selectedIndicator = indicators?.find(d => d.year === year && d.month === month);
  const selectedChartPoint = data.find(d => d.year === year && d.month === month);

  return (
    <div className="case-study__section pagasitikos-gulf-dashboard">
      <div className="pagasitikos-gulf-dashboard__header">
        <h2>
          <Icon className="pagasitikos-gulf-dashboard__title__icon" size="24px" icon={faWarning} />
          {t('One Health Dashboard')}
        </h2>
        <div className="pagasitikos-gulf-dashboard__header__selectors">
          <Select
            className="pagasitikos-gulf-dashboard__header__selectors__select"
            options={monthOptions}
            value={monthOptions.find(o => o?.id === month)}
            onInputChange={(opt) => {
              if (opt?.id == null) {
                return;
              }

              setMonth(opt.id);
            }}
            css={{ minWidth: '150px' }}
          />
          <Select
            className="pagasitikos-gulf-dashboard__header__selectors__select"
            options={yearOptions}
            value={yearOptions.find(o => o?.id === year)}
            onInputChange={(opt) => {
              const nextYear = opt?.id;

              if (nextYear == null) {
                return;
              }

              const nextYearMonths = monthsByYear[nextYear] ?? [];
              const nextMonth = nextYearMonths.includes(month)
                ? month
                : getClosestMonth(nextYearMonths);

              setYear(nextYear);
              setMonth(nextMonth);
            }}
          />
        </div>
      </div>
      <div className="pagasitikos-gulf-dashboard__indicators">
        {selectedIndicator
          ? (
              Object.entries(INDICATORS).map(([key, { label, color, icon, backgroundColor, valueBackgroundColor }]) => (
                <IndicatorCard
                  key={key}
                  label={label}
                  color={color}
                  icon={icon}
                  backgroundColor={backgroundColor}
                  valueBackgroundColor={valueBackgroundColor}
                  valueColor={getThreshold(selectedIndicator[key])?.color}
                  value={selectedIndicator[key]}
                  tooltip={getThreshold(selectedIndicator[key])?.interpretation}
                  suggestedAction={getThreshold(selectedIndicator[key])?.action}
                />
              ))
            )
          : (
              <p>{t('No data available for the selected month and year.')}</p>
            )}
      </div>
      <div className="pagasitikos-gulf-dashboard__body">
        <div className="pagasitikos-gulf-dashboard__body__graph">
          <IndicatorsGraph
            data={data}
            selectedDataPoint={selectedChartPoint}
          />
        </div>
        <div className="pagasitikos-gulf-dashboard__body__parameters">
          <ContributingParameters month={month} year={year} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
